/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import * as monaco from 'monaco-editor';
import { v4 as uuidv4 } from 'uuid';

import SelectorViewZone, { renderReactToElement } from '../SelectorViewZone';
import {
    FunctionMetadata,
    HealModelDecorationOptions,
    ParsedFunctionCall,
    VariablePosition,
} from '../types/LiveAgentEditorTypes';

import { MetadataManager } from './MetadataManager';

import { RecordedAction } from '@/pages/projects/LiveAgentTypes';

import '../styles/decorations.css';

const DEBUG_DECORATION = false;

/**
 * Interface representing decoration data to be stored in clipboard
 */
interface ClipboardDecorationData {
    decorations: Array<{
        range: monaco.IRange;
        healKey: string;
        hoverMessage: monaco.IMarkdownString | monaco.IMarkdownString[];
    }>;
}

/**
 * Interface for clipboard data combining text and decorations
 */
interface ClipboardData {
    decorations: ClipboardDecorationData;
    completeMetadata: FunctionMetadata[]; // Store complete metadata objects
    startLineNumber: number;
}

/**
 * Manages editor decorations for function calls in the Heal editor
 * Handles copy/paste of decorated code and provides functionality
 * for visualizing execution state of test steps
 */
export class DecorationManager {
    private editor: monaco.editor.IStandaloneCodeEditor;
    private metadataManager: MetadataManager;
    private decorationIds: string[] = [];
    private viewZoneIds: string[] = [];
    private viewZoneMap: Map<number, { id: string; selector: string }> = new Map();
    // Track the last paste operation to handle view zone adjustments
    private lastPasteInfo: { lineStart: number; lineCount: number } | null = null;
    // Separate array to track variable decoration IDs
    private variableDecorationIds: string[] = [];

    /**
     * Creates a new DecorationManager
     * @param editor The Monaco editor instance
     * @param metadataManager The metadata manager for function calls
     */
    constructor(editor: monaco.editor.IStandaloneCodeEditor, metadataManager: MetadataManager) {
        this.editor = editor;
        this.metadataManager = metadataManager;
    }

    /**
     * Handles keyboard events for copy and cut operations
     * Preserves decoration data when copying or cutting decorated text
     * @param e The keyboard event
     */
    onKeyDown(e: monaco.IKeyboardEvent): void {
        const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
        const isCopyCombo = (isMac && e.metaKey && e.code === 'KeyC') || (!isMac && e.ctrlKey && e.code === 'KeyC');
        const isCutCombo = (isMac && e.metaKey && e.code === 'KeyX') || (!isMac && e.ctrlKey && e.code === 'KeyX');

        if (isCopyCombo || isCutCombo) {
            const model = this.editor.getModel();
            if (!model) return;

            const selection = this.editor.getSelection();
            if (!selection) return;

            // Get decorations for the selected text
            const decorations = model.getDecorationsInRange(selection);
            const decorationData: ClipboardDecorationData = {
                decorations: decorations
                    .map((d) => {
                        const options = d.options as HealModelDecorationOptions;
                        return {
                            range: d.range,
                            healKey: options.description?.healKey || '',
                            hoverMessage: options.hoverMessage || [],
                        };
                    })
                    .filter((d) => d.healKey),
            };

            // Collect full metadata for selected decorations
            const fullMetadata = decorations
                .map((d) => {
                    const options = d.options as HealModelDecorationOptions;
                    if (options.description?.healKey) {
                        return this.metadataManager.getMetadataById(options.description?.healKey);
                    }
                    return undefined;
                })
                .filter((m): m is FunctionMetadata => m !== undefined);

            // Store decoration data in clipboard
            if (decorationData.decorations.length > 0) {
                const text = this.editor.getModel()?.getValueInRange(selection) || '';
                const data: ClipboardData = {
                    decorations: decorationData,
                    completeMetadata: fullMetadata,
                    startLineNumber: selection.startLineNumber,
                };

                // Store text and decoration data as separate MIME types
                const encodedMetadata = encodeURIComponent(JSON.stringify(data));
                const htmlContent = `<div style="display:none" data-decoration-metadata="${encodedMetadata}"></div>`;

                const clipboardItem = new ClipboardItem({
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                    'text/html': new Blob([htmlContent], { type: 'text/html' }),
                });

                navigator.clipboard.write([clipboardItem]).catch((error) => {
                    // eslint-disable-next-line no-console
                    console.error('Failed to copy decorations to clipboard', error);
                });

                // If it's a cut operation, remove the decorations but keep their history
                if (isCutCombo) {
                    const decorationIds = decorations.map((d) => d.id);
                    this.editor.deltaDecorations(decorationIds, []);
                }
            }
        }
    }

    /**
     * Handles paste events in the editor
     * Restores decorations when pasting previously copied/cut decorated text
     * @param e The paste event
     */
    async onDidPaste(e: monaco.editor.IPasteEvent): Promise<void> {
        try {
            // Try to get clipboard items with MIME types
            const clipboardItems = await navigator.clipboard.read();
            let decorationsData: ClipboardData | null = null;
            let text = '';

            // Look for our JSON data in the clipboard items
            for (const item of clipboardItems) {
                if (item.types.includes('text/html')) {
                    try {
                        const htmlBlob = await item.getType('text/html');
                        const htmlText = await htmlBlob.text();

                        // Parse HTML and look for our metadata div
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(htmlText, 'text/html');
                        const metadataDiv = doc.querySelector('[data-decoration-metadata]');

                        if (metadataDiv) {
                            const encodedData = metadataDiv.getAttribute('data-decoration-metadata');
                            if (encodedData) {
                                const jsonText = decodeURIComponent(encodedData);
                                decorationsData = JSON.parse(jsonText);

                                if (DEBUG_DECORATION) {
                                    // eslint-disable-next-line no-console
                                    console.log('Pasting decorations from HTML', decorationsData);
                                    // eslint-disable-next-line no-console
                                    console.log('Range', e.range);
                                }
                            }
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('Error parsing clipboard HTML data:', error);
                    }
                }

                if (item.types.includes('text/plain')) {
                    try {
                        const textBlob = await item.getType('text/plain');
                        text = await textBlob.text();
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('Error reading clipboard text data:', error);
                    }
                }
            }

            // If we have decoration data, process it
            if (decorationsData) {
                // Calculate the line offset between original and new positions
                const lineOffset = e.range.startLineNumber - decorationsData.startLineNumber;

                // Store paste information for view zone adjustment
                const lineCount = text.split('\n').length;
                this.lastPasteInfo = {
                    lineStart: e.range.startLineNumber,
                    lineCount: lineCount > 1 ? lineCount - 1 : 0, // Adjust for single line pastes
                };

                // Apply decorations to the pasted text with adjusted positions
                for (let i = 0; i < decorationsData.decorations.decorations.length; ++i) {
                    const decoration = decorationsData.decorations.decorations[i];
                    const metadataFromClipboard = decorationsData.completeMetadata[i];

                    const newRange = new monaco.Range(
                        decoration.range.startLineNumber + lineOffset,
                        decoration.range.startColumn,
                        decoration.range.endLineNumber + lineOffset,
                        decoration.range.endColumn,
                    );

                    // Only process if we have metadata from clipboard
                    if (metadataFromClipboard) {
                        // Create a new heal key for the new decoration
                        const healKey = uuidv4();

                        // Use complete metadata from clipboard
                        const metadata = structuredClone(metadataFromClipboard);

                        // Register the new metadata with MetadataManager using the new key
                        this.metadataManager.addMetadata(healKey, newRange, metadata);

                        if (DEBUG_DECORATION) {
                            // eslint-disable-next-line no-console
                            console.log('Added metadata from clipboard', healKey, metadata.actions, newRange);
                        }

                        // Reset execution status
                        metadata.executed = false;
                        metadata.running = false;
                        metadata.result = 'UNKNOWN';
                        metadata.queued = false;

                        const playwrightCurrentSelector = metadata?.actions?.find(
                            (a: RecordedAction) => a.selectors?.playwrightCurrent,
                        )?.selectors?.playwrightCurrent.code;

                        const newIds = this.editor.deltaDecorations(
                            [],
                            [
                                {
                                    range: newRange,
                                    options: {
                                        description: { healKey },
                                        stickiness: monaco.editor.TrackedRangeStickiness.AlwaysGrowsWhenTypingAtEdges,
                                    } as HealModelDecorationOptions,
                                },
                            ],
                        );
                        this.decorationIds.push(...newIds);

                        // Add viewzone for selector if available
                        if (playwrightCurrentSelector) {
                            this.addSelectorViewZone(newRange.startLineNumber, playwrightCurrentSelector);
                        }
                    }
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error during paste operation:', error);
        }
    }

    /**
     * Retrieves decoration options for a specific function call
     * @param call The parsed function call
     * @returns The decoration options if found, undefined otherwise
     */
    getHealDecorationOptions(call: ParsedFunctionCall): HealModelDecorationOptions | undefined {
        const model = this.editor.getModel();
        if (!model) return undefined;

        // We use decorationsPosition so we are sure the decoration is included in the range
        const decorations = model.getDecorationsInRange({
            startLineNumber: call.functionPosition.lineNumberStart,
            startColumn: call.functionPosition.columnStart,
            endLineNumber: call.functionPosition.lineNumberEnd,
            endColumn: call.functionPosition.columnEnd,
        });

        if (DEBUG_DECORATION) {
            // eslint-disable-next-line no-console
            console.log(
                'Heal keys found:',
                decorations.map((d) => (d.options as HealModelDecorationOptions).description?.healKey),
            );
        }

        return decorations?.find((d) => {
            const options = d.options as HealModelDecorationOptions;
            return options.description?.healKey;
        })?.options as HealModelDecorationOptions | undefined;
    }

    /**
     * Creates a view zone above a function call to display its selector
     * @param lineNumber The line number before which to add the view zone
     * @param selectorCode The selector code to display
     */
    private addSelectorViewZone(lineNumber: number, selectorCode: string): void {
        // Render the React component to a DOM element
        const domNode = renderReactToElement(SelectorViewZone, { selectorCode });

        this.editor.changeViewZones((changeAccessor) => {
            const id = changeAccessor.addZone({
                afterLineNumber: lineNumber,
                heightInLines: 1,
                domNode,
            });
            this.viewZoneIds.push(id);
            this.viewZoneMap.set(lineNumber, { id, selector: selectorCode });
        });
    }

    /**
     * Updates view zones by keeping existing ones that didn't change,
     * removing ones that are no longer needed, and adding new ones.
     * Handles line shifts caused by paste operations.
     * @param neededViewZones Map of line numbers to selector code for all needed view zones
     */
    private updateViewZones(neededViewZones: Map<number, string>): void {
        // Handle line shifts from paste operations
        if (this.lastPasteInfo) {
            const { lineStart, lineCount } = this.lastPasteInfo;

            // Create a new adjusted map to handle line shifts
            const adjustedMap = new Map<number, { id: string; selector: string }>();

            // Adjust line numbers for view zones below the paste point
            for (const [line, data] of this.viewZoneMap.entries()) {
                if (line >= lineStart) {
                    // Shift line number down by the number of lines inserted
                    adjustedMap.set(line + lineCount, data);
                    if (DEBUG_DECORATION) {
                        // eslint-disable-next-line no-console
                        console.log(`Adjusting view zone from line ${line} to ${line + lineCount}`);
                    }
                } else {
                    // Keep view zones above the paste point as is
                    adjustedMap.set(line, data);
                }
            }

            // Replace the view zone map with the adjusted one
            this.viewZoneMap = adjustedMap;

            // Reset last paste info
            this.lastPasteInfo = null;
        }

        // Track zones to remove and lines to process in a single pass
        const zonesToRemove: string[] = [];
        const linesToProcess = new Set([...this.viewZoneMap.keys(), ...neededViewZones.keys()]);

        // Process all lines in one pass
        linesToProcess.forEach((lineNumber) => {
            const existing = this.viewZoneMap.get(lineNumber);
            const neededSelector = neededViewZones.get(lineNumber);

            // Case 1: Line needs a view zone
            if (neededSelector) {
                // Case 1a: No existing view zone or different selector - create new
                if (!existing || existing.selector !== neededSelector) {
                    // If there's an existing one with different content, mark for removal
                    if (existing) {
                        zonesToRemove.push(existing.id);
                        this.viewZoneMap.delete(lineNumber);
                        this.viewZoneIds = this.viewZoneIds.filter((id) => id !== existing.id);
                    }

                    // Create the new view zone
                    const domNode = renderReactToElement(SelectorViewZone, { selectorCode: neededSelector });
                    this.editor.changeViewZones((changeAccessor) => {
                        const id = changeAccessor.addZone({
                            afterLineNumber: lineNumber,
                            heightInLines: 1,
                            domNode,
                        });
                        this.viewZoneIds.push(id);
                        this.viewZoneMap.set(lineNumber, { id, selector: neededSelector });
                    });
                }
                // Case 1b: Existing view zone with same selector - keep it (do nothing)
            }
            // Case 2: Line doesn't need a view zone but has one - remove it
            else if (existing) {
                zonesToRemove.push(existing.id);
                this.viewZoneMap.delete(lineNumber);
                this.viewZoneIds = this.viewZoneIds.filter((id) => id !== existing.id);
            }
        });

        // Remove all zones marked for removal
        if (zonesToRemove.length > 0) {
            this.editor.changeViewZones((changeAccessor) => {
                for (const id of zonesToRemove) {
                    changeAccessor.removeZone(id);
                }
            });
        }
    }

    /**
     * Creates decorations for function metadata in the editor
     * Sets visual indicators for execution state (running, passed, failed, etc.)
     * @param metadata The function metadata to create decorations for
     * @param isDisabled Whether the decorations should be disabled
     * @param hasOneRunningStepOrOneInQueue Whether there is a running step or one in queue
     * @param hidePlaywright Whether to hide playwright decorations
     * @param variablePositions The variable positions to create decorations for
     */
    createDecorations(
        metadata: Record<string, FunctionMetadata>,
        isDisabled: boolean,
        hasOneRunningStepOrOneInQueue: boolean,
        hidePlaywright: boolean,
        variablePositions: Map<string, VariablePosition[]>,
    ): void {
        const hoverDecorations = Object.values(metadata)
            .filter((meta) => meta.active)
            .map((meta) => {
                return {
                    range: new monaco.Range(
                        meta.call.functionPosition.lineNumberStart,
                        meta.call.functionPosition.columnStart,
                        meta.call.functionPosition.lineNumberEnd,
                        meta.call.functionPosition.columnEnd,
                    ),
                    options: {
                        description: { healKey: meta.key },
                        stickiness: monaco.editor.TrackedRangeStickiness.AlwaysGrowsWhenTypingAtEdges,
                    } as HealModelDecorationOptions,
                };
            });

        const lineDecorations = Object.values(metadata)
            .filter((meta) => meta.active)
            .map((meta, index) => {
                const className = meta.executed
                    ? meta.running
                        ? 'running'
                        : meta.result === 'PASS'
                          ? 'pass'
                          : 'error'
                    : meta.queued
                      ? 'queued'
                      : hasOneRunningStepOrOneInQueue
                        ? 'disabled'
                        : 'ready';

                if (DEBUG_DECORATION) {
                    // eslint-disable-next-line no-console
                    console.log('Drawing line at ', meta.call.decorationsPosition.lineNumberStart);
                }

                return {
                    range: new monaco.Range(
                        meta.call.decorationsPosition.lineNumberStart,
                        1,
                        meta.call.decorationsPosition.lineNumberStart,
                        1,
                    ),
                    options: {
                        isWholeLine: true,
                        className: `healStepLineDecoration`,
                        ...(isDisabled
                            ? {}
                            : { glyphMarginClassName: ['healStepExecutionButton', className].join(' ') }),
                        glyphMarginHoverMessage: {
                            value:
                                className === 'ready'
                                    ? '▶ Run...'
                                    : className === 'running'
                                      ? 'Running...'
                                      : meta.message,
                            isTrusted: true,
                        },
                    } as HealModelDecorationOptions,
                };
            });

        const allDecorations = [...hoverDecorations, ...lineDecorations];

        this.decorationIds = this.editor.deltaDecorations(this.decorationIds, allDecorations);

        // Collect needed view zones
        const neededViewZones = new Map<number, string>();
        if (!hidePlaywright) {
            Object.values(metadata)
                .filter((meta) => meta.active)
                .forEach((meta) => {
                    const playwrightCurrentSelector = meta.actions?.find(
                        (a: RecordedAction) => a.selectors?.playwrightCurrent,
                    )?.selectors?.playwrightCurrent.code;

                    if (playwrightCurrentSelector) {
                        neededViewZones.set(meta.call.functionPosition.lineNumberStart, playwrightCurrentSelector);
                    }
                });
        }

        // Update view zones (add new, keep existing, remove old)
        this.updateViewZones(neededViewZones);

        // Draw variable decorations
        this.drawVariableDecorations(variablePositions);
    }

    /**
     * Removes all decorations from the editor including variable decorations
     */
    clearDecorations(): void {
        this.decorationIds = this.editor.deltaDecorations(this.decorationIds, []);
        this.variableDecorationIds = this.editor.deltaDecorations(this.variableDecorationIds, []);
    }

    /**
     * Draws variable decorations in the editor and shows values on hover
     * @param variablePositions Map of variable names to their positions and values
     */
    drawVariableDecorations(variablePositions: Map<string, VariablePosition[]>): void {
        // Flatten the nested array structure
        const decorations = Array.from(variablePositions.entries()).flatMap(([variable, positions]) => {
            return positions.map((position) => {
                return {
                    range: new monaco.Range(
                        position.position.lineNumberStart,
                        position.position.columnStart,
                        position.position.lineNumberEnd,
                        position.position.columnEnd,
                    ),
                    options: {
                        stickiness: monaco.editor.TrackedRangeStickiness.AlwaysGrowsWhenTypingAtEdges,
                        hoverMessage: {
                            value: `**Value**: ${position.value || 'undefined'}`,
                            isTrusted: true,
                        },
                    } as HealModelDecorationOptions,
                };
            });
        });

        // Use a separate array to track variable decoration IDs
        this.variableDecorationIds = this.editor.deltaDecorations(this.variableDecorationIds, decorations);
    }
}
